import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Snackbar,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Storefront as StorefrontIcon,
  Work as WorkIcon,
  SupervisedUserCircle as SupervisedUserCircleIcon,
  Warehouse as WarehouseIcon,
  Devices as DevicesIcon,
  CreditCard as CreditCardIcon,
  MoreHoriz as MoreHorizIcon
} from '@mui/icons-material';
import {
  getPersonnel as getPersonnelSql,
  getManagerPanel,
  setManagerPanel,
} from '../../services/migration/personnelDataService';
import { getAssignmentsByBranch } from '../../services/migration/branchPersonnelAssignmentDataService';
import { hasBuyerB2BForBranch } from '../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

interface Permission {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  subPermissions?: Permission[];
}

// Alt izinleri genişletilebilir olan modüller (mevcut davranış korunuyor)
const EXPANDABLE_MODULES = ['dashboard', 'qrmenu', 'salesReports', 'accountingDashboard', 'warehouse', 'cash', 'bank', 'personnel', 'production'];

// Alt izinleri genişletme paneli yerine grup altında ayrı satırlar olarak
// gösterilen modüller (sidebar'daki buton düzeniyle birebir eşleşmesi için).
// Kayıt formatı değişmez: satırlar yine `<modul>_subPermissions` içine yazılır.
const SUBS_AS_ROWS_MODULES = ['accountingDashboard', 'warehouse', 'cash', 'bank', 'personnel', 'production'];

// Alt izin satırları hemen üstlerindeki modül satırının altında girintili gösterilir.
const NESTED_SUB_ROW_MODULES = ['cash', 'bank', 'personnel', 'production'];

// Başka bir alt izne bağlı alt izinler (çocuk -> ebeveyn). Ebeveyn kapalıyken
// çocuk seçilemez; ebeveyn kapatılınca çocuklar da kapanır. Backend aynı kuralı
// ManagerPanelPermissions.IsGranted içinde uygular.
const SUB_PERMISSION_PARENTS: Record<string, string> = {
  accountingDashboard_purchaseInvoiceCreate: 'accountingDashboard_invoices',
  accountingDashboard_purchaseInvoiceUpdate: 'accountingDashboard_invoices',
  accountingDashboard_purchaseInvoiceDelete: 'accountingDashboard_invoices',
  warehouse_adjust: 'warehouse_view',
  warehouse_movements: 'warehouse_view',
};

// Grup altındaki bir satır. Sidebar'daki buton sırasıyla birebir eşleşir:
// - module: mevcut izin anahtarına bağlı seçilebilir satır
// - sub: bir modülün alt iznine bağlı seçilebilir satır (kayıt formatı değişmez,
//   yine `<modul>_subPermissions` içine yazılır)
// - soon: henüz manager desteği olmayan sidebar butonu (pasif, seçilemez)
type GroupItem =
  | { kind: 'module'; id: string }
  | { kind: 'sub'; moduleId: string; subId: string }
  | { kind: 'soon'; id: string; name: string };

// Sidebar başlık yapısıyla aynı gruplar ve buton sıraları.
interface ModuleGroupDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: GroupItem[];
}

const MODULE_GROUPS: ModuleGroupDef[] = [
  {
    id: 'accounting',
    label: 'Ön Muhasebe',
    icon: <AccountBalanceWalletIcon />,
    items: [
      { kind: 'module', id: 'accountingDashboard' },
      { kind: 'module', id: 'current' },
      { kind: 'module', id: 'bank' },
      { kind: 'sub', moduleId: 'bank', subId: 'bank_transactions' },
      { kind: 'sub', moduleId: 'bank', subId: 'bank_creditCards' },
      { kind: 'sub', moduleId: 'bank', subId: 'bank_transfers' },
      { kind: 'module', id: 'cash' },
      { kind: 'sub', moduleId: 'cash', subId: 'cash_expense' },
      { kind: 'sub', moduleId: 'cash', subId: 'cash_movements' },
      { kind: 'soon', id: 'stock', name: 'Stok' },
      { kind: 'soon', id: 'checksNotes', name: 'Çek/Senet' },
      { kind: 'sub', moduleId: 'accountingDashboard', subId: 'accountingDashboard_invoices' },
      { kind: 'sub', moduleId: 'accountingDashboard', subId: 'accountingDashboard_purchaseInvoiceCreate' },
      { kind: 'sub', moduleId: 'accountingDashboard', subId: 'accountingDashboard_purchaseInvoiceUpdate' },
      { kind: 'sub', moduleId: 'accountingDashboard', subId: 'accountingDashboard_purchaseInvoiceDelete' },
      { kind: 'sub', moduleId: 'accountingDashboard', subId: 'accountingDashboard_eInvoices' },
      { kind: 'sub', moduleId: 'accountingDashboard', subId: 'accountingDashboard_profitLoss' },
      { kind: 'soon', id: 'offers', name: 'Teklifler' },
      { kind: 'soon', id: 'waybills', name: 'İrsaliyeler' },
      { kind: 'module', id: 'orders' },
      { kind: 'module', id: 'warehouse' },
      { kind: 'sub', moduleId: 'warehouse', subId: 'warehouse_view' },
      { kind: 'sub', moduleId: 'warehouse', subId: 'warehouse_adjust' },
      { kind: 'sub', moduleId: 'warehouse', subId: 'warehouse_movements' },
      { kind: 'module', id: 'production' },
      { kind: 'sub', moduleId: 'production', subId: 'production_create' },
      { kind: 'sub', moduleId: 'production', subId: 'production_complete' },
      { kind: 'sub', moduleId: 'production', subId: 'production_cancel' },
      { kind: 'sub', moduleId: 'production', subId: 'production_reports' },
      { kind: 'sub', moduleId: 'production', subId: 'production_planning' },
      { kind: 'sub', moduleId: 'production', subId: 'production_ops_setup' },
      { kind: 'sub', moduleId: 'production', subId: 'production_steps' },
      { kind: 'sub', moduleId: 'production', subId: 'production_quality' },
      { kind: 'soon', id: 'currency', name: 'Döviz' },
      { kind: 'sub', moduleId: 'accountingDashboard', subId: 'accountingDashboard_branchPrices' },
      { kind: 'soon', id: 'imageLibrary', name: 'Resim Kütüphanesi' }
    ]
  },
  {
    id: 'sales_system',
    label: 'Satış Sistemi',
    icon: <StorefrontIcon />,
    items: [
      { kind: 'soon', id: 'kitchenScreen', name: 'Mutfak Ekranı' },
      { kind: 'soon', id: 'fastSale', name: 'Hızlı Satış' },
      { kind: 'soon', id: 'restaurant', name: 'Restoran' },
      { kind: 'soon', id: 'orderManagement', name: 'Paket Yönetim Sistemi' },
      { kind: 'module', id: 'qrmenu' },
      { kind: 'soon', id: 'kioskMenu', name: 'Kiosk Menü' },
      { kind: 'soon', id: 'eCommerce', name: 'E-Ticaret' },
      { kind: 'soon', id: 'courier', name: 'Kurye' },
      { kind: 'module', id: 'salesReports' },
      { kind: 'soon', id: 'integrations', name: 'Entegrasyonlar' },
      { kind: 'soon', id: 'clubEntrance', name: 'Kulüp Giriş' },
      { kind: 'soon', id: 'imageLibrary', name: 'Resim Kütüphanesi' }
    ]
  },
  {
    id: 'task_tracking',
    label: 'Görev Takip',
    icon: <WorkIcon />,
    items: [
      { kind: 'module', id: 'dashboard' },
      { kind: 'module', id: 'tasks' },
      { kind: 'module', id: 'reports' },
      { kind: 'module', id: 'messages' },
      { kind: 'module', id: 'surveys' }
    ]
  },
  {
    id: 'hr',
    label: 'İnsan Kaynakları',
    icon: <SupervisedUserCircleIcon />,
    items: [
      { kind: 'module', id: 'personnel' },
      { kind: 'sub', moduleId: 'personnel', subId: 'personnel_create' },
      { kind: 'sub', moduleId: 'personnel', subId: 'personnel_transactions' },
      { kind: 'module', id: 'shifts' },
      { kind: 'soon', id: 'leaveManagement', name: 'İzin Yönetimi' },
      { kind: 'soon', id: 'performanceManagement', name: 'Performans Yönetimi' },
      { kind: 'soon', id: 'trainingDevelopment', name: 'Eğitim & Gelişim' },
      { kind: 'soon', id: 'payroll', name: 'Bordro' },
      { kind: 'soon', id: 'documentManagement', name: 'Doküman Yönetimi' },
      { kind: 'soon', id: 'recruitment', name: 'İşe Alım' },
      { kind: 'soon', id: 'cvApplications', name: 'CV Başvuruları' }
    ]
  },
  {
    id: 'wms',
    label: 'Akıllı WMS Depo',
    icon: <WarehouseIcon />,
    items: [
      { kind: 'soon', id: 'wmsPanel', name: 'WMS Panel' },
      { kind: 'soon', id: 'wmsWarehouseSetup', name: 'Depo Tanımlama' },
      { kind: 'soon', id: 'wmsOrderPool', name: 'Sipariş Havuzu' },
      { kind: 'soon', id: 'wmsWaves', name: 'İş Emirleri' },
      { kind: 'soon', id: 'wmsPickingSetup', name: 'Toplama Ayarları' },
      { kind: 'soon', id: 'wmsCancelMgmt', name: 'İptal Yönetimi' },
      { kind: 'soon', id: 'wmsPackingSetup', name: 'Paketleme Ayarları' },
      { kind: 'soon', id: 'wmsReceivingSetup', name: 'Mal Kabul Ayarları' },
      { kind: 'soon', id: 'wmsCycleCountSetup', name: 'Stok Sayım Ayarları' },
      { kind: 'soon', id: 'wmsReceivingMgmt', name: 'Mal Kabul Yönetimi' },
      { kind: 'soon', id: 'wmsCycleCountMgmt', name: 'Stok Sayım Yönetimi' },
      { kind: 'soon', id: 'wmsEquipment', name: 'Ekipman Yönetimi' },
      { kind: 'soon', id: 'wmsReports', name: 'WMS Raporları' }
    ]
  },
  {
    id: 'device_management',
    label: 'Cihaz Yönetimi',
    icon: <DevicesIcon />,
    items: [{ kind: 'soon', id: 'deviceManagement', name: 'Cihaz Yönetimi' }]
  },
  {
    id: 'card_management',
    label: 'Kart & Cüzdan',
    icon: <CreditCardIcon />,
    items: [{ kind: 'soon', id: 'cards', name: 'Kartlar' }]
  },
  {
    id: 'other',
    label: 'Diğer',
    icon: <MoreHorizIcon />,
    items: [
      { kind: 'soon', id: 'reservations', name: 'Rezervasyonlar' },
      { kind: 'soon', id: 'events', name: 'Etkinlikler' },
      { kind: 'soon', id: 'mailReports', name: 'Mail Raporları' }
    ]
  }
];

interface BranchManager {
  id: string;
  name: string;
  email: string;
  role: string;
  // Birincil şubesi bu şube olan yönetici mi, yoksa ek atama ile mi bağlı?
  isPrimary: boolean;
}

interface ManagerPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  branchId: string;
  branchName: string;
  companyId: string;
}

const ManagerPermissionsModal: React.FC<ManagerPermissionsModalProps> = ({
  open,
  onClose,
  branchId,
  branchName,
  companyId
}) => {
  const { t } = useTranslation('branches');
  const { isModuleEnabled } = useAuth();
  const productionVisible = isModuleEnabled('production');
  const productionOpsVisible = isModuleEnabled('production_ops');
  const loadedSettingsRef = useRef<any>(null);
  const [branchHasB2B, setBranchHasB2B] = useState(false);
  const [managers, setManagers] = useState<BranchManager[]>([]);
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  // Grup (sidebar başlığı) açık/kapalı durumları — varsayılan hepsi kapalı
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  // Kullanılabilir modüller (izinler)
  const [modules, setModules] = useState<Permission[]>([
    {
      id: 'dashboard',
      name: 'Dashboard',
      description: 'Ana ekran ve özet bilgiler',
      enabled: false,
      subPermissions: [
        { id: 'dashboard_stat_cards', name: 'İstatistik Kartları', description: 'Personel, görev, ilerleme istatistikleri', enabled: false },
        { id: 'dashboard_task_status', name: 'Günlük Görev Durumu Dağılımı', description: 'Günlük görevlerin durum dağılımı grafiği', enabled: false },
        { id: 'dashboard_weekly_task_status', name: 'Haftalık Görev Durumu Dağılımı', description: 'Haftalık görevlerin durum dağılımı grafiği', enabled: false },
        { id: 'dashboard_monthly_task_status', name: 'Aylık Görev Durumu Dağılımı', description: 'Aylık görevlerin durum dağılımı grafiği', enabled: false },
        { id: 'dashboard_personnel_performance', name: 'Personel Performansı - Günlük Görevler', description: 'Personellerin günlük görev performans grafiği', enabled: false },
        { id: 'dashboard_weekly_personnel_performance', name: 'Personel Performansı - Haftalık Görevler', description: 'Personellerin haftalık görev performans grafiği', enabled: false },
        { id: 'dashboard_monthly_personnel_performance', name: 'Personel Performansı - Aylık Görevler', description: 'Personellerin aylık görev performans grafiği', enabled: false },
        { id: 'dashboard_survey_charts', name: 'Anket Grafikleri', description: 'Anket sonuçları grafikleri', enabled: false },
        { id: 'dashboard_missed_tasks', name: 'Geciken Son 10 Görev', description: 'Geciken görevler listesi', enabled: false },
        { id: 'dashboard_completed_tasks', name: 'Tamamlanan Son 10 Görev', description: 'Tamamlanan görevler listesi', enabled: false },
        { id: 'dashboard_worst_performers', name: 'En Çok Görev Geciktiren Personeller', description: 'Geciken görevleri olan personeller', enabled: false },
        { id: 'dashboard_best_performers', name: 'En Çok Görev Yapan Personeller', description: 'Tamamlanan görevleri olan personeller', enabled: false },
        { id: 'dashboard_task_locations', name: 'Görev Tamamlama Konumları (Harita)', description: 'Görev tamamlama konumları haritası', enabled: false }
      ]
    },
    {
      id: 'tasks',
      name: 'Görevler',
      description: 'Görev yönetimi ve takibi',
      enabled: false,
      subPermissions: [
        { id: 'tasks_view', name: 'Görüntüleme', description: 'Görevleri görüntüleme', enabled: false },
        { id: 'tasks_create', name: 'Oluşturma', description: 'Yeni görev oluşturma', enabled: false },
        { id: 'tasks_edit', name: 'Düzenleme', description: 'Görevleri düzenleme', enabled: false },
        { id: 'tasks_delete', name: 'Silme', description: 'Görevleri silme', enabled: false }
      ]
    },
    {
      id: 'personnel',
      name: 'Personel',
      description: 'Personel yönetimi',
      enabled: false,
      subPermissions: [
        { id: 'personnel_view', name: 'Görüntüleme', description: 'Personelleri görüntüleme', enabled: false },
        { id: 'personnel_create', name: 'Personel oluşturma', description: 'Yalnız kendi şubesine yeni personel (çalışan rolüyle) oluşturma', enabled: false },
        { id: 'personnel_edit', name: 'Düzenleme', description: 'Personel bilgilerini düzenleme', enabled: false },
        { id: 'personnel_transactions', name: 'Personel hareketleri', description: 'Şube personeline maaş/prim hakedişi, maaş/prim ödemesi, avans, borç/alacak ve ödeme/tahsilat (yalnız şubenin kasa/banka hesaplarıyla)', enabled: false }
      ]
    },
    {
      id: 'shifts',
      name: 'Vardiya',
      description: 'Vardiya planlaması ve yönetimi',
      enabled: false,
      subPermissions: [
        { id: 'shifts_view', name: 'Görüntüleme', description: 'Vardiyaları görüntüleme', enabled: false },
        { id: 'shifts_create', name: 'Oluşturma', description: 'Yeni vardiya oluşturma', enabled: false },
        { id: 'shifts_edit', name: 'Düzenleme', description: 'Vardiyaları düzenleme', enabled: false }
      ]
    },
    {
      id: 'reports',
      name: 'Raporlar',
      description: 'Raporlar ve analizler',
      enabled: false,
      subPermissions: [
        { id: 'reports_performance', name: 'Performans', description: 'Performans raporları', enabled: false },
        { id: 'reports_tasks', name: 'Görev Raporları', description: 'Görev istatistikleri', enabled: false },
        { id: 'reports_export', name: 'Dışa Aktarma', description: 'Rapor dışa aktarma', enabled: false }
      ]
    },
    {
      id: 'messages',
      name: 'Mesajlar',
      description: 'Mesajlaşma sistemi',
      enabled: false,
      subPermissions: [
        { id: 'messages_send', name: 'Mesaj Gönderme', description: 'Mesaj gönderme yetkisi', enabled: false },
        { id: 'messages_receive', name: 'Mesaj Alma', description: 'Mesaj alma yetkisi', enabled: false }
      ]
    },
    {
      id: 'surveys',
      name: 'Anketler',
      description: 'Anket yönetimi',
      enabled: false,
      subPermissions: [
        { id: 'surveys_view', name: 'Görüntüleme', description: 'Anketleri görüntüleme', enabled: false },
        { id: 'surveys_create', name: 'Oluşturma', description: 'Anket oluşturma', enabled: false },
        { id: 'surveys_results', name: 'Sonuçlar', description: 'Anket sonuçlarını görme', enabled: false }
      ]
    },
    {
      id: 'qrmenu',
      name: 'QR Menü',
      description: 'QR Menü yönetimi ve masa siparişleri',
      enabled: false,
      subPermissions: [
        { id: 'qrmenu_view', name: 'Görüntüleme', description: 'QR menüleri görüntüleme', enabled: false },
        { id: 'qrmenu_create', name: 'Oluşturma', description: 'Yeni QR menü oluşturma', enabled: false },
        { id: 'qrmenu_edit', name: 'Düzenleme', description: 'QR menü ayarlarını düzenleme', enabled: false },
        { id: 'qrmenu_delete', name: 'Silme', description: 'QR menüleri silme', enabled: false },
        { id: 'qrmenu_orders', name: 'Masa Siparişleri', description: 'Masa siparişlerini görüntüleme ve yönetme', enabled: false },
        { id: 'qrmenu_kitchen', name: 'Mutfak Ekranı', description: 'Mutfak ekranına erişim', enabled: false },
        { id: 'qrmenu_tables', name: 'Masa Yönetimi', description: 'Masa ekleme ve yönetme', enabled: false },
        { id: 'qrmenu_customers', name: 'Müşteri Yönetimi', description: 'Müşteri bilgilerini görüntüleme', enabled: false },
        { id: 'qrmenu_daily_closing', name: 'Gün Sonu İşlemleri', description: 'Gün sonu alma ve raporlama', enabled: false }
      ]
    },
    {
      id: 'salesReports',
      name: 'Satış Raporları',
      description: 'Gün sonu ve satış raporlarına erişim',
      enabled: false,
      subPermissions: [
        { id: 'salesReports_endOfDay', name: 'Gün Sonu Raporu', description: 'Gün sonu alma ve görüntüleme', enabled: false },
        { id: 'salesReports_view', name: 'Rapor Görüntüleme', description: 'Geçmiş satış raporlarını görüntüleme', enabled: false },
        { id: 'salesReports_export', name: 'Dışa Aktarma', description: 'Raporları Excel/PDF olarak dışa aktarma', enabled: false }
      ]
    },
    {
      id: 'accountingDashboard',
      name: 'Şube Yönetimi Paneli',
      description: 'Ön muhasebe gösterge paneli',
      enabled: false,
      subPermissions: [
        { id: 'accountingDashboard_view', name: 'Gösterge Paneli', description: 'Ön muhasebe gösterge panelini görüntüleme', enabled: false },
        { id: 'accountingDashboard_invoices', name: 'Faturalar', description: 'Faturaları görüntüleme ve yönetme', enabled: false },
        { id: 'accountingDashboard_purchaseInvoiceCreate', name: 'Alış Faturası Ekleme', description: 'Kendi şubesi adına yeni alış faturası girme', enabled: false },
        { id: 'accountingDashboard_purchaseInvoiceUpdate', name: 'Alış Faturası Düzenleme', description: 'Kendi şubesinin alış faturalarını düzenleme', enabled: false },
        { id: 'accountingDashboard_purchaseInvoiceDelete', name: 'Alış Faturası Silme', description: 'Kendi şubesinin alış faturalarını silme', enabled: false },
        { id: 'accountingDashboard_eInvoices', name: 'E-Fatura', description: 'E-Faturaları görüntüleme ve yönetme', enabled: false },
        { id: 'accountingDashboard_profitLoss', name: 'Kâr-Zarar Raporu', description: 'Sadece kendi şubesi; merkez giderleri görünmez', enabled: false },
        { id: 'accountingDashboard_branchPrices', name: 'Şube Fiyatları', description: 'Şube fiyatlarını görüntüleme ve düzenleme', enabled: false }
      ]
    },
    {
      id: 'current',
      name: 'Cari Hesaplar',
      description: 'Cari hesaplar ve cari hareketler (yalnızca kendi şubesi)',
      enabled: false
    },
    {
      id: 'bank',
      name: 'Banka',
      description: 'Banka hesaplarını ve hareketlerini görme, yeni hesap ekleme (yalnızca kendi şubesi)',
      enabled: false,
      subPermissions: [
        { id: 'bank_transactions', name: 'Para yatır / çek', description: 'Şubeye bağlı banka hesaplarına para yatırma ve gider türüyle para çekme (banka gideri)', enabled: false },
        { id: 'bank_creditCards', name: 'Kredi kartı işlemleri', description: 'Şube adına kredi kartı ekleme, kart harcaması (gider türü zorunlu) ve şube kasası/hesabından borç ödeme', enabled: false },
        { id: 'bank_transfers', name: 'Virman ve transferler', description: 'Şubenin kendi banka hesapları arasında ve şube kasası ile banka arasında transfer', enabled: false }
      ]
    },
    {
      id: 'cash',
      name: 'Kasa',
      description: 'Kasaları listeleme ve yeni kasa ekleme (yalnızca kendi şubesi)',
      enabled: false,
      subPermissions: [
        { id: 'cash_expense', name: 'Kasadan gider ekle', description: 'Şube kasasından gün içi nakit harcamaları gider olarak kaydetme (sebep zorunlu)', enabled: false },
        { id: 'cash_movements', name: 'Kasa hareket raporu', description: 'Yalnızca kendi şubesine bağlı kasaların hareketlerini görüntüleme ve Excel indirme', enabled: false }
      ]
    },
    {
      id: 'orders',
      name: 'Siparişler',
      description: 'B2B siparişleri: şube adına eşleştirme kodu alma ve şubenin gelen alış siparişleri',
      enabled: false
    },
    {
      id: 'warehouse',
      name: 'Depo',
      description: 'Şubeye bağlı depolar (transfer ve depo tanımlama kapalı)',
      enabled: false,
      subPermissions: [
        { id: 'warehouse_view', name: 'Görüntüle', description: 'Şube depolarının stok listesi', enabled: false },
        { id: 'warehouse_adjust', name: 'Miktar düzelt', description: 'Sayım veya fark girerek stok miktarını düzeltme (sebep zorunlu)', enabled: false },
        { id: 'warehouse_movements', name: 'Stok hareketleri', description: 'Şube depolarının stok hareket raporu ve Excel çıktısı', enabled: false }
      ]
    },
    {
      id: 'production',
      name: 'Üretim',
      description: 'Şube depolarında üretim fişleri ve ürün ağacı',
      enabled: false,
      subPermissions: [
        { id: 'production_create', name: 'Fiş oluşturma', description: 'Taslak üretim fişi oluşturma, düzenleme ve silme', enabled: false },
        { id: 'production_complete', name: 'Fiş tamamlama', description: 'Üretimi tamamlama (stoktan düşer, ürünü depoya alır)', enabled: false },
        { id: 'production_cancel', name: 'Fiş iptali', description: 'Tamamlanmış üretimi iptal etme (ters stok hareketi)', enabled: false },
        { id: 'production_reports', name: 'Üretim raporları', description: 'Şube depolarının üretim raporları', enabled: false },
        { id: 'production_planning', name: 'Planlama', description: 'İhtiyaç planı hesaplama ve seçilen üretim önerilerinden taslak fiş oluşturma', enabled: false },
        { id: 'production_ops_setup', name: 'Operasyon tanımları', description: 'İş istasyonu ve operasyon kütüphanesi', enabled: false },
        { id: 'production_steps', name: 'Adım onayı', description: 'Üretim adımı onaylama, bitirme ve geri alma', enabled: false },
        { id: 'production_quality', name: 'Kalite onayı', description: 'Kalan engelleyici ölçümü gerekçeyle onaylama', enabled: false },
      ]
    }
  ]);

  // Şubedeki yöneticileri getir
  useEffect(() => {
    if (open && companyId && branchId) {
      fetchBranchManagers();
      (async () => {
        try {
          // Bu şube bir B2B satıcıya giriş yaptıysa (aktif alıcı bağlantısı varsa)
          // Siparişler o şube için otomatik ve kilitli açıktır.
          const on = await hasBuyerB2BForBranch(branchId);
          setBranchHasB2B(on);
          if (on) {
            setModules(prev => prev.map(m => (m.id === 'orders' ? { ...m, enabled: true } : m)));
          }
        } catch (e) {
          console.warn('[ManagerPermissionsModal] B2B şube bağları okunamadı:', e);
          setBranchHasB2B(false);
        }
      })();
    }
  }, [open, companyId, branchId]);

  // Seçilen yöneticiye ait izinleri getir
  useEffect(() => {
    if (selectedManager) {
      fetchManagerPermissions();
    } else {
      // Seçilen yönetici yoksa tüm izinleri devre dışı bırak
      resetPermissions();
    }
  }, [selectedManager]);

  // Grup başlığı aç/kapa işleyicisi
  const handleToggleGroup = (groupId: string) => {
    setOpenGroups(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  // Alt modül genişletme/daraltma işleyicisi
  const handleExpandModule = (moduleId: string) => {
    if (EXPANDABLE_MODULES.includes(moduleId)) {
      if (expandedModules.includes(moduleId)) {
        setExpandedModules(expandedModules.filter(id => id !== moduleId));
      } else {
        setExpandedModules([...expandedModules, moduleId]);
      }
    }
  };

  // Modül ve alt izinleri değiştirme işleyicisi
  const handleModuleChange = (moduleId: string, checked: boolean) => {
    if (moduleId === 'orders' && branchHasB2B) {
      return;
    }
    setModules(prevModules => {
      return prevModules.map(module => {
        if (module.id === moduleId) {
          // Ana modülü güncelle
          const updatedModule = { ...module, enabled: checked };
          
          // Eğer ana modül devre dışı bırakıldıysa tüm alt izinleri de devre dışı bırak
          if (!checked && EXPANDABLE_MODULES.includes(moduleId) && module.subPermissions) {
            updatedModule.subPermissions = module.subPermissions.map(subPerm => ({
              ...subPerm,
              enabled: false
            }));
          }

          // Alt izinleri satır olarak gösterilen modüllerde (ör. Ön Muhasebe)
          // ana satır "gösterge paneli" iznini temsil eder -> view alt iznini eşitle.
          if (checked && SUBS_AS_ROWS_MODULES.includes(moduleId) && module.subPermissions) {
            updatedModule.subPermissions = (updatedModule.subPermissions || module.subPermissions).map(subPerm =>
              subPerm.id === `${moduleId}_view` ? { ...subPerm, enabled: true } : subPerm
            );
          }
          
          // Genişletilebilir modüller için alt izin panelini otomatik toggle et
          if (EXPANDABLE_MODULES.includes(moduleId) && !SUBS_AS_ROWS_MODULES.includes(moduleId)) {
            if (expandedModules.includes(moduleId)) {
              setExpandedModules(prev => prev.filter(id => id !== moduleId));
            } else {
              setExpandedModules(prev => [...prev, moduleId]);
            }
          }
          
          return updatedModule;
        }
        return module;
      });
    });
  };

  // Alt izinleri değiştirme işleyicisi
  const handleSubPermissionChange = (moduleId: string, permissionId: string, checked: boolean) => {
    if (EXPANDABLE_MODULES.includes(moduleId)) {
      setModules(prevModules => {
        return prevModules.map(module => {
          if (module.id === moduleId && module.subPermissions) {
            // Alt izni güncelle
            const parentId = SUB_PERMISSION_PARENTS[permissionId];
            if (checked && parentId && !module.subPermissions.find(p => p.id === parentId)?.enabled) {
              return module;
            }
            const updatedSubPermissions = module.subPermissions.map(subPerm => {
              if (subPerm.id === permissionId) {
                return { ...subPerm, enabled: checked };
              }
              if (!checked && SUB_PERMISSION_PARENTS[subPerm.id] === permissionId) {
                return { ...subPerm, enabled: false };
              }
              return subPerm;
            });
            
            // Eğer herhangi bir alt izin etkinleştirilirse ana modülü de etkinleştir
            const shouldEnableModule = updatedSubPermissions.some(p => p.enabled);
            // Depo'da görüntüleme kapanırsa modül de kapanır (backend aynı kuralı uygular).
            const viewTurnedOff = moduleId === 'warehouse' && permissionId === 'warehouse_view' && !checked;
            
            return {
              ...module,
              enabled: viewTurnedOff ? false : (shouldEnableModule || module.enabled),
              subPermissions: updatedSubPermissions
            };
          }
          return module;
        });
      });
    }
  };

  // Bir modülün tüm alt izinlerini seç/temizle
  const handleSelectAllSubPermissions = (moduleId: string, select: boolean) => {
    if (EXPANDABLE_MODULES.includes(moduleId)) {
      setModules(prevModules => {
        return prevModules.map(module => {
          if (module.id === moduleId && module.subPermissions) {
            // Tüm alt izinleri güncelle
            const updatedSubPermissions = module.subPermissions.map(subPerm => ({
              ...subPerm,
              enabled: select
            }));
            
            return {
              ...module,
              enabled: select || module.enabled, // Ana modül en az bir alt izin seçiliyse aktif olmalı
              subPermissions: updatedSubPermissions
            };
          }
          return module;
        });
      });
    }
  };

  // Şubedeki yöneticileri getirme.
  // Bir yönetici birden çok şubeye atanabildiği için şubenin yönetici listesi iki
  // kaynaktan oluşur: birincil şubesi burası olanlar (personel kaydındaki
  // `branchesId`) ve bu şubeye ek atama ile bağlananlar (şube-personel atamaları).
  // Şube kartındaki "Yöneticiler" listesi de aynı iki kaynağı birleştirir; ikisinin
  // aynı kişileri göstermesi için burada da aynı birleştirme yapılır.
  const fetchBranchManagers = async () => {
    setLoadingManagers(true);
    
    try {
      // Faz 3: personel listesi SQL'den.
      const sqlPersonnel = await getPersonnelSql(companyId);
      const personnelList: any[] = Array.isArray(sqlPersonnel) ? sqlPersonnel : [];

      // Ek atamalar en iyi çaba ile okunur: bu istek başarısız olsa bile birincil
      // yöneticiler listelenmeye devam eder.
      let assignedUserIds = new Set<string>();
      try {
        const assignments = await getAssignmentsByBranch(branchId);
        assignedUserIds = new Set(assignments.map(a => a.userId).filter(Boolean));
      } catch (assignmentError) {
        console.warn('[ManagerPermissionsModal] Ek şube atamaları okunamadı:', assignmentError);
      }

      const managersMap = new Map<string, BranchManager>();
      personnelList.forEach((data: any) => {
        if (!data?.id || data.isDeleted) return;

        const isPrimary = data.branchesId === branchId && data.role === 'manager';
        if (!isPrimary && !assignedUserIds.has(data.id)) return;

        managersMap.set(data.id, {
          id: data.id, // Firebase UID
          name: data.name || t('managerPermissions.defaults.unnamedManager'),
          email: data.email || '',
          role: data.role || 'manager',
          isPrimary
        });
      });

      const managersList = Array.from(managersMap.values()).sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return a.name.localeCompare(b.name, 'tr');
      });
      setManagers(managersList);

      // Tek yönetici varsa doğrudan seç; aksi halde seçim yalnızca listede hâlâ
      // yer alıyorsa korunur.
      setSelectedManager(prev => {
        if (managersList.length === 1) return managersList[0].id;
        return managersList.some(m => m.id === prev) ? prev : '';
      });
    } catch (error) {
      console.error('Şube yöneticileri yüklenirken hata:', error);
      setSnackbarMessage(t('managerPermissions.messages.loadManagersError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingManagers(false);
    }
  };

  // Yönetici izinlerini getirme
  const fetchManagerPermissions = async () => {
    if (!selectedManager) return;
    
    setLoadingPermissions(true);
    
    try {
      // Faz 3: yonetici paneli izinleri SQL'den (managerPanel screenType, SettingsJson); HybridFallback ile RTDB.
      let permissionsData: any = null;
      try {
        const mp = await getManagerPanel(selectedManager);
        if (mp.found && mp.settings) permissionsData = mp.settings;
      } catch (e) {
        console.warn('[ManagerPermissionsModal] SQL managerPanel okuma hatasi:', e);
      }
      loadedSettingsRef.current = permissionsData;
      if (permissionsData) {
        // Modülleri ve alt izinleri güncelle
        updateModulesFromPermissions(permissionsData);
        
        // Dashboard veya QR Menü modülü aktifse otomatik genişlet
        if (permissionsData['dashboard'] === true && !expandedModules.includes('dashboard')) {
          setExpandedModules(prev => [...prev, 'dashboard']);
        }
        if (permissionsData['qrmenu'] === true && !expandedModules.includes('qrmenu')) {
          setExpandedModules(prev => [...prev, 'qrmenu']);
        }
        if (permissionsData['accountingDashboard'] === true && !expandedModules.includes('accountingDashboard')) {
          setExpandedModules(prev => [...prev, 'accountingDashboard']);
        }
      } else {
        // İzin bulunamadıysa tüm izinleri devre dışı bırak
        resetPermissions();
      }
    } catch (error) {
      console.error('Yönetici izinleri yüklenirken hata:', error);
      setSnackbarMessage(t('managerPermissions.messages.loadPermissionsError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      resetPermissions();
    } finally {
      setLoadingPermissions(false);
    }
  };

  // İzinleri modüllere uygulama
  const updateModulesFromPermissions = (permissionsData: any) => {
    setModules(prevModules => {
      return prevModules.map(module => {
        // Ana modül iznini kontrol et
        const moduleEnabled = permissionsData[module.id] === true;
        
        // Genişletilebilir modüllerin alt izinlerini kontrol et
        let updatedSubPermissions = module.subPermissions;
        if (module.id === 'warehouse' && module.subPermissions) {
          // warehouse_view kaydı yoksa modül açıkken görüntüleme açık sayılır.
          const saved = permissionsData['warehouse_subPermissions'] || {};
          updatedSubPermissions = module.subPermissions.map(subPerm => ({
            ...subPerm,
            enabled: subPerm.id === 'warehouse_view'
              ? moduleEnabled && saved.warehouse_view !== false
              : moduleEnabled && saved[subPerm.id] === true
          }));
        } else if (EXPANDABLE_MODULES.includes(module.id) && module.subPermissions && permissionsData[module.id + '_subPermissions']) {
          updatedSubPermissions = module.subPermissions.map(subPerm => {
            const subPermEnabled = permissionsData[module.id + '_subPermissions'][subPerm.id] === true;
            return { ...subPerm, enabled: subPermEnabled };
          });
        }
        
        return {
          ...module,
          enabled: module.id === 'orders' && branchHasB2B ? true : moduleEnabled,
          subPermissions: updatedSubPermissions
        };
      });
    });
  };

  // Tüm izinleri sıfırlama
  const resetPermissions = () => {
    setModules(prevModules => {
      return prevModules.map(module => {
        if (EXPANDABLE_MODULES.includes(module.id)) {
          return {
            ...module,
            enabled: false,
            subPermissions: module.subPermissions ? module.subPermissions.map(subPerm => ({
              ...subPerm,
              enabled: false
            })) : undefined
          };
        } else {
          return {
            ...module,
            enabled: module.id === 'orders' && branchHasB2B
          };
        }
      });
    });
  };

  // İzinleri kaydetme
  const handleSavePermissions = async () => {
    if (!selectedManager || !companyId) {
      return;
    }
    
    setSavingPermissions(true);
    
    try {
      // İzinleri formatla
      const formattedPermissions: any = {};
      
      modules.forEach(module => {
        // Ana modül iznini ekle
        formattedPermissions[module.id] =
          module.id === 'orders' && branchHasB2B ? true : module.enabled;
        
        // Genişletilebilir modüllerin alt izinlerini ekle
        if (EXPANDABLE_MODULES.includes(module.id) && module.subPermissions && module.subPermissions.length > 0) {
          formattedPermissions[module.id + '_subPermissions'] = {};
          
          module.subPermissions.forEach(subPerm => {
            formattedPermissions[module.id + '_subPermissions'][subPerm.id] = subPerm.enabled;
          });
        }
      });

      // Modül satırları gizliyken kayıtlı üretim yetkileri silinmesin.
      if (!productionVisible) {
        delete formattedPermissions.production;
        delete formattedPermissions.production_subPermissions;
        const raw = loadedSettingsRef.current;
        if (raw && Object.prototype.hasOwnProperty.call(raw, 'production')) {
          formattedPermissions.production = raw.production;
        }
        if (raw?.production_subPermissions && typeof raw.production_subPermissions === 'object') {
          formattedPermissions.production_subPermissions = raw.production_subPermissions;
        }
      } else if (!productionOpsVisible && formattedPermissions.production_subPermissions) {
        const rawSubs = loadedSettingsRef.current?.production_subPermissions;
        for (const key of ['production_ops_setup', 'production_steps', 'production_quality']) {
          if (rawSubs && typeof rawSubs === 'object' && Object.prototype.hasOwnProperty.call(rawSubs, key)) {
            formattedPermissions.production_subPermissions[key] = rawSubs[key];
          } else {
            delete formattedPermissions.production_subPermissions[key];
          }
        }
      }

      // Faz 3: SQL'e kaydet (managerPanel screenType, modul haritasi SettingsJson).
      await setManagerPanel(selectedManager, true, formattedPermissions);

      setSnackbarMessage(t('managerPermissions.messages.saveSuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // ✅ Başarılı kaydetme sonrası modalı kapat
      setTimeout(() => {
        onClose();
      }, 1000); // 1 saniye bekle ki kullanıcı mesajı görsün
    } catch (error: any) {
      console.error('Yönetici izinleri kaydedilirken hata:', error);
      setSnackbarMessage(t('managerPermissions.messages.saveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSavingPermissions(false);
    }
  };

  // Modal kapatma işleyicisi
  const handleClose = () => {
    setSelectedManager('');
    setBranchHasB2B(false);
    resetPermissions();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              {t('managerPermissions.title', { branchName })}
            </Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            {t('managerPermissions.descriptionPrimary')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('managerPermissions.descriptionSecondary')}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Yönetici Seçimi */}
        <FormControl fullWidth sx={{ mb: 4 }}>
          <InputLabel id="manager-select-label">{t('managerPermissions.selectManager')}</InputLabel>
          <Select
            labelId="manager-select-label"
            value={selectedManager}
            label={t('managerPermissions.selectManager')}
            onChange={(e) => setSelectedManager(e.target.value as string)}
            disabled={loadingManagers}
            displayEmpty={managers.length === 0}
            renderValue={(selected) => {
              if (selected === '') {
                return <Typography color="text.secondary">{t('managerPermissions.pleaseSelectManager')}</Typography>;
              }
              
              const manager = managers.find(m => m.id === selected);
              return manager ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                    {manager.name.charAt(0)}
                  </Avatar>
                  <Typography>{manager.name}</Typography>
                  {manager.email && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({manager.email})
                    </Typography>
                  )}
                </Box>
              ) : '';
            }}
          >
            {loadingManagers ? (
              <MenuItem disabled>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                {t('managerPermissions.loadingManagers')}
              </MenuItem>
            ) : managers.length === 0 ? (
              <MenuItem disabled>
                {t('managerPermissions.noManagers')}
              </MenuItem>
            ) : (
              managers.map((manager) => (
                <MenuItem key={manager.id} value={manager.id}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>{manager.name.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{manager.name}</Typography>
                        {!manager.isPrimary && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="info"
                            label={t('managerPermissions.extraAssignment', { defaultValue: 'Ek atama' })}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={manager.email}
                  />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        
        {/* İzin Modülleri */}
        {selectedManager ? (
          loadingPermissions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('managerPermissions.modulePermissions')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('managerPermissions.modulePermissionsHint')}
              </Typography>
              
              <List component="div" sx={{ width: '100%' }}>
                {MODULE_GROUPS.map((group) => {
                  const groupOpen = openGroups.includes(group.id);
                  // Rozet: gruptaki aktif modül + aktif alt izin satırı sayısı
                  const enabledCount = group.items.reduce((acc, item) => {
                    if (!productionVisible && (
                      (item.kind === 'module' && item.id === 'production') ||
                      (item.kind === 'sub' && item.moduleId === 'production')
                    )) return acc;
                    if (item.kind === 'sub' && (item.subId === 'production_ops_setup' || item.subId === 'production_steps' || item.subId === 'production_quality') && !productionOpsVisible) return acc;
                    if (item.kind === 'module') {
                      return acc + (modules.find(m => m.id === item.id)?.enabled ? 1 : 0);
                    }
                    if (item.kind === 'sub') {
                      const parent = modules.find(m => m.id === item.moduleId);
                      const sub = parent?.subPermissions?.find(p => p.id === item.subId);
                      return acc + (parent?.enabled && sub?.enabled ? 1 : 0);
                    }
                    return acc;
                  }, 0);
                  return (
                    <Box key={group.id} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                      {/* Grup başlığı (sidebar başlığı karşılığı) */}
                      <ListItem
                        onClick={() => handleToggleGroup(group.id)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: 'rgba(16, 38, 72, 0.06)',
                          '&:hover': { bgcolor: 'rgba(16, 38, 72, 0.1)' }
                        }}
                        secondaryAction={
                          <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleToggleGroup(group.id); }}>
                            {groupOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        }
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5, color: 'primary.main' }}>
                          {group.icon}
                        </Box>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {t(`managerPermissions.groups.${group.id}`, { defaultValue: group.label })}
                              </Typography>
                              {enabledCount > 0 && (
                                <Chip size="small" color="primary" label={enabledCount} sx={{ height: 20 }} />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>

                      <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                        {group.items.map((item) => {
                          if (!productionVisible && (
                            (item.kind === 'module' && item.id === 'production') ||
                            (item.kind === 'sub' && item.moduleId === 'production')
                          )) return null;
                          if (item.kind === 'sub' && (item.subId === 'production_ops_setup' || item.subId === 'production_steps' || item.subId === 'production_quality') && !productionOpsVisible) return null;
                          // Pasif (henüz manager desteği olmayan) sidebar butonu
                          if (item.kind === 'soon') {
                            return (
                              <ListItem
                                key={`soon-${item.id}`}
                                sx={{ pl: 4, opacity: 0.5, borderTop: 1, borderColor: 'divider' }}
                                secondaryAction={<Checkbox checked={false} disabled />}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="subtitle2" color="text.secondary">
                                        {t(`managerPermissions.comingSoonModules.${item.id}`, { defaultValue: item.name })}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={t('managerPermissions.comingSoon', { defaultValue: 'Yakında' })}
                                        sx={{ height: 20, fontSize: 11 }}
                                      />
                                    </Box>
                                  }
                                />
                              </ListItem>
                            );
                          }

                          // Alt izne bağlı satır (ör. Faturalar, E-Fatura, Şube Fiyatları)
                          if (item.kind === 'sub') {
                            const parent = modules.find(m => m.id === item.moduleId);
                            const sub = parent?.subPermissions?.find(p => p.id === item.subId);
                            if (!parent || !sub) return null;
                            const parentSubId = SUB_PERMISSION_PARENTS[sub.id];
                            const parentSubOff = !!parentSubId &&
                              !parent.subPermissions?.find(p => p.id === parentSubId)?.enabled;
                            const nestedUnderModule = NESTED_SUB_ROW_MODULES.includes(item.moduleId);
                            return (
                              <ListItem
                                key={`sub-${item.subId}`}
                                sx={{
                                  pl: nestedUnderModule || parentSubId ? 8 : 4,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                  opacity: parentSubOff ? 0.5 : (nestedUnderModule && !parent.enabled ? 0.7 : 1),
                                  bgcolor: sub.enabled ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                  ...(nestedUnderModule && {
                                    borderTopStyle: 'dashed',
                                    position: 'relative',
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      left: 40,
                                      top: 0,
                                      bottom: 0,
                                      borderLeft: 2,
                                      borderColor: parent.enabled ? 'primary.light' : 'divider',
                                    },
                                  }),
                                }}
                                secondaryAction={
                                  <Checkbox
                                    checked={sub.enabled}
                                    disabled={parentSubOff}
                                    onChange={(_, checked) =>
                                      handleSubPermissionChange(item.moduleId, item.subId, checked)
                                    }
                                  />
                                }
                              >
                                <ListItemText
                                  primary={
                                    <Typography variant="subtitle2" fontWeight={500}>
                                      {t(`managerPermissions.permissions.${sub.id}.name`, { defaultValue: sub.name })}
                                    </Typography>
                                  }
                                  secondary={t(`managerPermissions.permissions.${sub.id}.description`, { defaultValue: sub.description })}
                                />
                              </ListItem>
                            );
                          }

                          // Manager destekli modül satırı (seçilebilir)
                          const module = modules.find(m => m.id === item.id);
                          if (!module) return null;
                          const expandable = EXPANDABLE_MODULES.includes(module.id) &&
                            !SUBS_AS_ROWS_MODULES.includes(module.id) &&
                            module.subPermissions && module.subPermissions.length > 0;
                          const ordersLocked = module.id === 'orders' && branchHasB2B;
                          return (
                            <Box key={module.id} sx={{ borderTop: 1, borderColor: 'divider' }}>
                              <ListItem
                                secondaryAction={
                                  <Checkbox
                                    checked={ordersLocked ? true : module.enabled}
                                    disabled={ordersLocked}
                                    onChange={(_, checked) => handleModuleChange(module.id, checked)}
                                  />
                                }
                                sx={{ pl: 4, bgcolor: (ordersLocked || module.enabled) ? 'rgba(25, 118, 210, 0.08)' : 'inherit' }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="subtitle2" fontWeight={500}>
                                        {t(`managerPermissions.modules.${module.id}.name`, { defaultValue: module.name })}
                                      </Typography>
                                      {ordersLocked && (
                                        <Chip
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          label={t('managerPermissions.ordersAlwaysOn', { defaultValue: 'Şirket genelinde açık' })}
                                          sx={{ height: 20, fontSize: 11 }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    ordersLocked
                                      ? t('managerPermissions.ordersAlwaysOnHint')
                                      : t(`managerPermissions.modules.${module.id}.description`, { defaultValue: module.description })
                                  }
                                />

                                {expandable && (
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleExpandModule(module.id)}
                                    sx={{ ml: 1 }}
                                  >
                                    {expandedModules.includes(module.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                )}
                              </ListItem>

                              {expandable && (
                                <Collapse in={expandedModules.includes(module.id)} timeout="auto" unmountOnExit>
                                  <Box sx={{ pl: 6, pr: 2, py: 1, bgcolor: 'background.default' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                      <Typography variant="subtitle2">
                                        {t('managerPermissions.subPermissions')}
                                      </Typography>
                                      {module.enabled && (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => handleSelectAllSubPermissions(module.id, true)}
                                          >
                                            {t('managerPermissions.selectAll')}
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleSelectAllSubPermissions(module.id, false)}
                                          >
                                            {t('managerPermissions.clearAll')}
                                          </Button>
                                        </Box>
                                      )}
                                    </Box>

                                    <FormGroup>
                                      {module.subPermissions!.map((permission) => (
                                        <FormControlLabel
                                          key={permission.id}
                                          control={
                                            <Checkbox
                                              checked={permission.enabled}
                                              onChange={(_, checked) =>
                                                handleSubPermissionChange(module.id, permission.id, checked)
                                              }
                                              disabled={!module.enabled}
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography variant="body2">{t(`managerPermissions.permissions.${permission.id}.name`, { defaultValue: permission.name })}</Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {t(`managerPermissions.permissions.${permission.id}.description`, { defaultValue: permission.description })}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                      ))}
                                    </FormGroup>
                                  </Box>
                                </Collapse>
                              )}
                            </Box>
                          );
                        })}
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            </Box>
          )
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('managerPermissions.selectManagerToEdit')}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSavePermissions}
          disabled={!selectedManager || savingPermissions || loadingPermissions}
          startIcon={savingPermissions && <CircularProgress size={24} color="inherit" />}
        >
          {savingPermissions ? t('common.saving') : t('managerPermissions.savePermissions')}
        </Button>
      </DialogActions>
      
      {/* Bildirim */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ManagerPermissionsModal; 